import { bearing } from '@turf/bearing';
import { point } from '@turf/helpers';
import { __, compose, includes, not, prop, find, toLower, last, propEq } from 'ramda';

import { findBoundsOfWaypoints } from '@utils/bounds.ts';
import { getDisplayDistance } from '@utils/distance.ts';
import { distance as getGeoDistanceMetres, bearingToDirection } from '@utils/geodesy.ts';

import SEGMENT_BADGES from './segmentBadges.ts';
import SEGMENT_CATEGORIES from './segmentCategories.ts';
import type { Segment, TranslateFn, Waypoint } from './types.ts';

/* eslint-disable @typescript-eslint/no-explicit-any -- legacy step builder works with dynamic route segment objects */

/**
 * Calculate the general direction of a segment based on its waypoints.
 * @param {Object} segment - The segment object.
 * @param {Array} segment.waypoints - An array of waypoints.
 * waypoint {
 * position: { lat, lng, floorId, ordinal, structureId },
 * }
 * @returns {string} - The general direction of the segment.
 */

const calculateSegmentGeneralDirection = ({ waypoints }: { waypoints: Waypoint[] }, T: TranslateFn) => {
  const [firstWaypoint] = waypoints;
  const lastWaypoint = waypoints[waypoints.length - 1];
  const from = point([firstWaypoint.position.lng, firstWaypoint.position.lat]);
  const to = point([lastWaypoint.position.lng, lastWaypoint.position.lat]);
  const bearingDegrees = bearing(from, to);
  // Convert bearing to general direction
  return bearingToDirection(bearingDegrees, T); // returns cardinal direction 'north', 'northeast' etc as a string
};

/**
 * @typedef Step
 * @property {string} primaryText
 * @property {string} secondaryText
 * @property {string} icon
 * @property {Position} animationAnchor
 * @property {number} eta
 * @property {number} distance
 * @property {boolean} isAccessible
 * @property {Bounds} bounds
 * @property {SecurityWaitTime} securityWaitTimes
 *
 * @typedef Position
 * @property {number} lat
 * @property {number} lng
 * @property {string} floorId
 * @property {number} ordinal
 * @property {string} structureId
 *
 * @param {Segment[]} segments
 * @param {string} startName
 * @param {string} destinationName
 * @param floorIdToNameMap
 * @param {function} T - translate function
 * @param {QueueTypes} queueTypes
 * @param {boolean} requiresAccessibility
 * @return {Step[]} steps - list of navigation steps
 */
function getSteps(
  segments: Segment[],
  startName = '',
  destinationName = '',
  floorIdToNameMap: Record<string, string>,
  T: TranslateFn,
  queueTypes: any = {},
  requiresAccessibility: boolean,
  securityPois: any[] = [],
  currentUnits = 'feet',
) {
  return segments.map((segment, index) => {
    const securityWaitTimes = findPropWaypoints('securityWaitTimes')(segment.waypoints);
    const eta =
      securityWaitTimes && !securityWaitTimes.isTemporarilyClosed // if there is dynamic wait time and checkpoint is open, then use it
        ? securityWaitTimes.queueTime
        : Math.round(calculateSegmentEta(segment.waypoints));
    const distanceInMeters = calculateSegmentDistance(segment.waypoints);
    const distance = Math.round(distanceInMeters); // this is meters
    const displayDistance = getDisplayDistance(distanceInMeters, currentUnits);
    const icon = getIcon(segment.segmentCategory);
    const animationAnchor = getAnimationAnchor(segment.segmentCategory, segment.waypoints);
    const direction = calculateSegmentGeneralDirection(segment, T); // returns string like 'north', 'southwest', etc.
    const primaryText = getPrimaryText(
      segments,
      index,
      startName,
      destinationName,
      floorIdToNameMap,
      T,
      securityPois,
      queueTypes,
    );
    const secondaryText = getSecondaryText(segment, eta, T, false); // this is old but apparently relied on by customers using JS SDK and Mobile SDK
    const secondaryTextUI = getSecondaryText(
      segment,
      eta,
      T,
      true,
      direction,
      displayDistance.unit,
      displayDistance.distance,
    ); // this is used in Webengine UI
    const bounds = findBoundsOfWaypoints(segment.waypoints);
    const isAccessible = checkIfAccessible(segment);

    const step: any = {
      primaryText,
      secondaryText,
      secondaryTextUI,
      icon,
      animationAnchor,
      eta,
      distance,
      bounds,
      isAccessible,
      securityWaitTimes,
    };

    if (segment.poiId) {
      step.poiId = segment.poiId;
    }

    return step;
  });
}

function calculateSegmentEta(waypoints: Waypoint[]) {
  if (waypoints.length === 1) {
    return waypoints[0].eta;
  } // for stairs/elevator segments. should be removed once segment builder will be refactored to not append waypoint from previous semgnent
  return waypoints
    .map(prop('eta'))
    .slice(1) // first waypoint is end of previous segment and is added only to connect segments for navline calculation
    .reduce((segmentEta: number, eta: number) => segmentEta + eta, 0.0);
}

function calculateSegmentDistance(waypoints: Waypoint[]) {
  if (waypoints.length === 1) {
    return waypoints[0].distance;
  } // for stairs/elevator segments. should be removed once segment builder will be refactored to not append waypoint from previous semgnent
  return waypoints
    .map(waypoint => waypoint.distance)
    .slice(1) // first waypoint is end of previous segment and is added only to connect segments for navline calculation
    .reduce((segmentDistance: number, distance: number) => segmentDistance + distance, 0.0);
}

function getIcon(segmentCategory: string) {
  switch (segmentCategory) {
    case SEGMENT_CATEGORIES.START:
      return SEGMENT_BADGES.START;
    case SEGMENT_CATEGORIES.WALKING_TO_END:
      return SEGMENT_BADGES.END;
    case SEGMENT_CATEGORIES.ELEVATOR:
      return SEGMENT_BADGES.ELEVATOR;
    case SEGMENT_CATEGORIES.ELEVATOR_UP:
      return SEGMENT_BADGES.ELEVATOR_UP;
    case SEGMENT_CATEGORIES.ELEVATOR_DOWN:
      return SEGMENT_BADGES.ELEVATOR_DOWN;
    case SEGMENT_CATEGORIES.STAIRS:
      return SEGMENT_BADGES.STAIRS;
    case SEGMENT_CATEGORIES.STAIRS_UP:
      return SEGMENT_BADGES.STAIRS_UP;
    case SEGMENT_CATEGORIES.STAIRS_DOWN:
      return SEGMENT_BADGES.STAIRS_DOWN;
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS:
      return SEGMENT_BADGES.ACCESSIBLE_STAIRS;
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS_UP:
      return SEGMENT_BADGES.ACCESSIBLE_STAIRS_UP;
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS_DOWN:
      return SEGMENT_BADGES.ACCESSIBLE_STAIRS_DOWN;
    case SEGMENT_CATEGORIES.ESCALATOR:
      return SEGMENT_BADGES.ESCALATOR;
    case SEGMENT_CATEGORIES.ESCALATOR_UP:
      return SEGMENT_BADGES.ESCALATOR_UP;
    case SEGMENT_CATEGORIES.ESCALATOR_DOWN:
      return SEGMENT_BADGES.ESCALATOR_DOWN;
    case SEGMENT_CATEGORIES.WALKING_TO_PORTAL:
    case SEGMENT_CATEGORIES.WALK:
    case SEGMENT_CATEGORIES.WALK_DOWN:
    case SEGMENT_CATEGORIES.WALK_UP:
      return SEGMENT_BADGES.WALK;
    case SEGMENT_CATEGORIES.TRAIN:
      return SEGMENT_BADGES.TRAIN;
    case SEGMENT_CATEGORIES.TRAIN_UP:
      return SEGMENT_BADGES.TRAIN_UP;
    case SEGMENT_CATEGORIES.TRAIN_DOWN:
      return SEGMENT_BADGES.TRAIN_DOWN;
    case SEGMENT_CATEGORIES.BUS:
      return SEGMENT_BADGES.BUS;
    case SEGMENT_CATEGORIES.BUS_UP:
      return SEGMENT_BADGES.BUS_UP;
    case SEGMENT_CATEGORIES.BUS_DOWN:
      return SEGMENT_BADGES.BUS_DOWN;
    case SEGMENT_CATEGORIES.SECURITY_CHECKPOINT:
      return SEGMENT_BADGES.SECURITY_CHECKPOINT;
    case SEGMENT_CATEGORIES.RAMP:
      return SEGMENT_BADGES.RAMP;
    case SEGMENT_CATEGORIES.RAMP_UP:
      return SEGMENT_BADGES.RAMP_UP;
    case SEGMENT_CATEGORIES.RAMP_DOWN:
      return SEGMENT_BADGES.RAMP_DOWN;
    default:
      return SEGMENT_BADGES.WALK;
  }
}

function getAnimationAnchor(segmentCategory: string, waypoints: Waypoint[]) {
  let index;
  switch (segmentCategory) {
    case SEGMENT_CATEGORIES.START:
      index = 0;
      break;
    case SEGMENT_CATEGORIES.WALKING_TO_END:
      index = waypoints.length - 1;
      break;
    case SEGMENT_CATEGORIES.WALKING_TO_PORTAL:
      index = Math.min(waypoints.length - 1, Math.ceil(waypoints.length / 2));
      break;
    default:
      index = waypoints.length - 1;
  }
  return waypoints[index].position;
}

function getPrimaryText(
  segments: Segment[],
  index: number,
  startName: string,
  destinationName: string,
  floorIdToNameMap: Record<string, string>,
  T: TranslateFn,
  securityPois: any[],
  queueTypes: any,
) {
  const segment = segments[index];
  switch (segment.segmentCategory) {
    case SEGMENT_CATEGORIES.START:
      return getPositionName(segment.waypoints[0].position, startName);
    case SEGMENT_CATEGORIES.WALKING_TO_END:
      return getPositionName(segment.waypoints[segment.waypoints.length - 1].position, destinationName);
    case SEGMENT_CATEGORIES.WALKING_TO_SECURITY_CHECKPOINT: {
      const lookAheadWaypoints = segments[index + 1].waypoints;
      const laneName = getSecurityLaneName(queueTypes, lookAheadWaypoints);
      const securityLane = laneName ? T(`wayfinder:Security Lane`, { laneName }) : null;

      const poiTitle = getClosestSecurityPoiTitle(lookAheadWaypoints, securityPois);
      const displayName = securityLane || poiTitle; // Use laneName if defined; otherwise, poiTitle
      return displayName || T(`wayfinder:${segment.type}`);
    }
    case SEGMENT_CATEGORIES.WALKING_TO_PORTAL:
      return T(`wayfinder:${segments[index + 1].type}`);
    case SEGMENT_CATEGORIES.SECURITY_CHECKPOINT: {
      const laneName = getSecurityLaneName(queueTypes, segment.waypoints);
      const securityLane = laneName ? T(`wayfinder:Security Lane`, { laneName }) : null;
      const poiTitle = getClosestSecurityPoiTitle(segment.waypoints, securityPois);
      const displayName = securityLane || poiTitle; // Use securityLane if defined; otherwise, poiTitle
      return displayName || getLevelName(segment, floorIdToNameMap);
    }
    case SEGMENT_CATEGORIES.ELEVATOR:
    case SEGMENT_CATEGORIES.ELEVATOR_DOWN:
    case SEGMENT_CATEGORIES.ELEVATOR_UP:
    case SEGMENT_CATEGORIES.ESCALATOR:
    case SEGMENT_CATEGORIES.ESCALATOR_DOWN:
    case SEGMENT_CATEGORIES.ESCALATOR_UP:
    case SEGMENT_CATEGORIES.STAIRS:
    case SEGMENT_CATEGORIES.STAIRS_DOWN:
    case SEGMENT_CATEGORIES.STAIRS_UP:
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS:
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS_DOWN:
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS_UP:
      return getLevelName(segment, floorIdToNameMap);
    default:
      return T(`wayfinder:${segment.type}`);
  }
}

function getPositionName(position: any, poiName: string) {
  if (position.name) {
    return position.name;
  }
  return poiName;
}

function getLevelName(segment: Segment, floorIdToNameMap: Record<string, string>) {
  return floorIdToNameMap[(last(segment.waypoints) as Waypoint).position.floorId];
}

function getSecondaryText(
  segment: Segment,
  minutes: number,
  T: TranslateFn,
  ui: boolean,
  direction?: string,
  currentUnits?: string,
  currentUnitsDistance?: number,
) {
  const zeroOrOtherKeys = translateZeroOrOther(minutes, T);

  const travelVerb = 'Proceed';
  switch (segment.segmentCategory) {
    case SEGMENT_CATEGORIES.START:
      return T('wayfinder:Begin route at');
    case SEGMENT_CATEGORIES.ELEVATOR:
      return T('wayfinder:Take elevator to');
    case SEGMENT_CATEGORIES.ELEVATOR_UP:
      return T('wayfinder:Take elevator up to');
    case SEGMENT_CATEGORIES.ELEVATOR_DOWN:
      return T('wayfinder:Take elevator down to');
    case SEGMENT_CATEGORIES.STAIRS:
      return T('wayfinder:Take stairs to');
    case SEGMENT_CATEGORIES.STAIRS_UP:
      return T('wayfinder:Take stairs up to');
    case SEGMENT_CATEGORIES.STAIRS_DOWN:
      return T('wayfinder:Take stairs down to');
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS:
      return T('wayfinder:Take accessible stairs to');
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS_UP:
      return T('wayfinder:Take accessible stairs up to');
    case SEGMENT_CATEGORIES.ACCESSIBLE_STAIRS_DOWN:
      return T('wayfinder:Take accessible stairs down to');
    case SEGMENT_CATEGORIES.ESCALATOR:
      return T('wayfinder:Take escalator to');
    case SEGMENT_CATEGORIES.ESCALATOR_UP:
      return T('wayfinder:Take escalator up to');
    case SEGMENT_CATEGORIES.ESCALATOR_DOWN:
      return T('wayfinder:Take escalator down to');
    case SEGMENT_CATEGORIES.WALK:
    case SEGMENT_CATEGORIES.WALKING_TO_SECURITY_CHECKPOINT:
    case SEGMENT_CATEGORIES.WALKING_TO_PORTAL:
    case SEGMENT_CATEGORIES.WALKING_TO_END: {
      if (!ui) {
        return zeroOrOtherKeys(`${travelVerb} <1 minute to`, `${travelVerb} xx minute to`);
      } else {
        return createSecondaryTextWithHeading(currentUnitsDistance, currentUnits, direction, T);
      }
    }
    case SEGMENT_CATEGORIES.WALK_DOWN:
      return zeroOrOtherKeys(`${travelVerb} <1 minute down to`, `${travelVerb} xx minute down to`);
    case SEGMENT_CATEGORIES.WALK_UP:
      return zeroOrOtherKeys(`${travelVerb} <1 minute up to`, `${travelVerb} xx minute up to`);
    case SEGMENT_CATEGORIES.TRAIN:
      return zeroOrOtherKeys('Take train <1 minute', 'Take train xx minute');
    case SEGMENT_CATEGORIES.BUS:
      return zeroOrOtherKeys('Take bus <1 minute', 'Take bus xx minute');
    case SEGMENT_CATEGORIES.SECURITY_CHECKPOINT: {
      return T('wayfinder:Through');
    }
    case SEGMENT_CATEGORIES.RAMP:
      return T('wayfinder:Take ramp to');
    case SEGMENT_CATEGORIES.RAMP_UP:
      return T('wayfinder:Take ramp up to');
    case SEGMENT_CATEGORIES.RAMP_DOWN:
      return T('wayfinder:Take ramp down to');
    default:
      return '';
  }
}

const createSecondaryTextWithHeading = (
  currentUnitsDistance: number | undefined,
  currentUnits: string | undefined,
  direction: string | undefined,
  T: TranslateFn,
) => {
  const options = { direction, count: currentUnitsDistance };
  switch (currentUnits) {
    case 'feet':
      return T('wayfinder:Proceed __direction__ __count__ foot to', options);
    case 'miles':
      return T('wayfinder:Proceed __direction__ __count__ mile to', options);
    case 'kilometers':
      return T('wayfinder:Proceed __direction__ __count__ kilometer to', options);
    default:
      return T('wayfinder:Proceed __direction__ __count__ meter to', options);
  }
};

const translateZeroOrOther = (count: number, T: TranslateFn) => (zeroKey: string, otherKey: string) =>
  count === 0 ? T('wayfinder:' + zeroKey) : T('wayfinder:' + otherKey, { count });

const checkIfAccessible = compose(not, includes(__, ['escalator', 'stairs']), toLower, prop('type'));

const getSecurityLaneName = (queueTypes: any, waypoints: Waypoint[]) => {
  const lane = findPropWaypoints('securityLane')(waypoints);
  if (!lane) {
    return;
  }
  const types = prop(lane.type, queueTypes);
  const laneType = find(propEq(lane.id, 'id'), types) as any;
  return laneType?.displayText;
};

const findPropWaypoints = (propName: string) => (waypoints: Waypoint[]) =>
  waypoints.slice(1).find(waypoint => waypoint[propName])?.[propName]; // because first waypoint is the end of previous segment and can be security checkpoint

/**
 * Given an array of waypoints (with exactly one having portalType "Security Checkpoint")
 * and an array of securityPois, this function uses the checkpoint's position
 * to determine the closest security POI.
 *
 * @param {Array} waypoints - Array of waypoint objects.
 * @param {Array} securityPois - Array of security POI objects.
 * @returns {string|null} - The name of the closest security POI, or null if none found.
 */
const getClosestSecurityPoiTitle = (waypoints: Waypoint[], securityPois: any[]) => {
  if (!waypoints || waypoints.length === 0) {
    return null;
  }

  const checkpointWaypoint = waypoints[0];
  const { lat, lng, floorId } = checkpointWaypoint.position;
  // Use all POIs with a valid position on the same floor as the checkpoint.
  const sameFloorCandidates = securityPois.filter(poi => poi.position && poi.position.floorId === floorId);
  let closest: any = null;
  let minDistance = Infinity;
  sameFloorCandidates.forEach((poi: any) => {
    const d = getGeoDistanceMetres(lat, lng, poi.position.latitude, poi.position.longitude);
    if (d < minDistance) {
      minDistance = d;
      closest = poi;
    }
  });
  return closest ? closest.name : null;
};

export default getSteps;
export const internal = {
  getIcon,
  getClosestSecurityPoiTitle,
  calculateSegmentGeneralDirection,
};
